import { has } from 'lodash';
import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import {
  BULK_OUTLET_DO_SHOW_LOG,
  BULK_OUTLET_LOG_CHANGE_PAGE,
  BULK_OUTLET_LOG_SET_SHOWN,
  BULK_OUTLET_UPDATE_CONFIRM_PROCESS_RESPOND,
  DO_BULK_OUTLET_UPDATE,
} from 'state/actionTypes';
import { formatIso8601DateTime } from 'utils/dateTimeUtils';
import * as api from 'api/bulkOutletUpdateExpiration';
import ProcessStatus from 'constants/bulkAction/processStatus';
import UserConcurrentStatus from 'constants/userConcurrent/status';
import {
  clearLogErrors,
  initializeProcessState,
  prependOutletError,
  resetLogData,
  setCurrentOutletId,
  setGlobalError,
  setIsProcessing,
  setLogData,
  setLogErrors,
  setLogIsLoading,
  setLogModalShown,
  setLogPage,
  setProcessedCount,
  setProcessStatus,
  showConfirmProcessModal,
} from 'state/bulkOutletUpdateExpiration/actions';
import { cancelRequestedSelector } from 'state/bulkOutletUpdateExpiration/selectors';
import CustomError from 'utils/CustomError';

export function* doBulkOutletUpdate({ payload }) {
  const { outletIds, expirationDate, reason } = payload;
  const expiredAt = formatIso8601DateTime(expirationDate);

  yield put(showConfirmProcessModal());
  const {
    payload: { proceed },
  } = yield take(BULK_OUTLET_UPDATE_CONFIRM_PROCESS_RESPOND);
  if (!proceed) {
    return;
  }

  yield put(setProcessStatus(ProcessStatus.InProgress));
  yield put(initializeProcessState(expirationDate, outletIds));
  yield put(setIsProcessing(true));

  let processed = 0;
  let errorCount = 0;
  let isGlobalError = false;
  let wasAborted = false;
  let token = null;
  let event = UserConcurrentStatus.Start;
  const isOnce = outletIds.length === 1;
  for (let i = 0; i < outletIds.length; i++) {
    if (i !== 0 && i !== outletIds.length - 1 && token) {
      event = null;
    } else if (outletIds.length > 1 && i === outletIds.length - 1) {
      event = UserConcurrentStatus.End;
    }

    const outletId = outletIds[i];
    yield put(setCurrentOutletId(outletId));

    try {
      const response = yield call(
        api.updateOutletExpiration,
        event,
        token,
        outletId,
        expiredAt,
        isOnce,
        reason
      );
      if (event === UserConcurrentStatus.Start && !isOnce) {
        token = response.headers['x-token'];
      }
    } catch (e) {
      if (e instanceof CustomError) {
        yield put(prependOutletError(outletId, e.message));
        errorCount += 1;
        if (!token && has(e.info, 'token')) {
          token = e.info.token;
        }
      } else if (e instanceof Error) {
        isGlobalError = true;
        yield put(setGlobalError(e.message));
        break;
      }
    }
    processed += 1;
    yield put(setProcessedCount(processed));

    const cancelRequested = yield select(cancelRequestedSelector);
    if (cancelRequested) {
      wasAborted = true;
      break;
    }
  }

  if (isGlobalError) {
    yield put(setProcessStatus(ProcessStatus.Error));
  } else if (errorCount === processed) {
    yield put(setProcessStatus(ProcessStatus.AllFailed));
  } else if (processed === outletIds.length && errorCount > 0) {
    yield put(setProcessStatus(ProcessStatus.CompletedWithWarning));
  } else if (processed < outletIds.length && wasAborted) {
    yield put(setProcessStatus(ProcessStatus.Aborted));
  } else {
    yield put(setProcessStatus(ProcessStatus.Completed));
  }
}

function* logModalTask() {
  while (true) {
    const { page } = (yield take(BULK_OUTLET_LOG_CHANGE_PAGE)).payload;
    yield put(setLogIsLoading(true));
    yield put(clearLogErrors());
    yield put(resetLogData());
    try {
      const {
        data: {
          data: { internal_tools_activities: activities },
          paging,
        },
      } = yield call(api.getBulkOutletUpdateLogs, page, 20);
      yield put(setLogData(activities, paging));
    } catch (e) {
      yield put(setLogErrors(e.message));
    } finally {
      yield put(setLogIsLoading(false));
    }
  }
}

export function* doShowLogs() {
  const task = yield fork(logModalTask);
  yield delay(30);
  yield put(setLogPage(1));
  yield put(setLogModalShown(true));
  let closeModalRequest;
  do {
    closeModalRequest = (yield take(BULK_OUTLET_LOG_SET_SHOWN)).payload;
  } while (closeModalRequest.shown);
  yield cancel(task);
}

export default function*() {
  yield takeLatest(DO_BULK_OUTLET_UPDATE, doBulkOutletUpdate);
  yield takeLatest(BULK_OUTLET_DO_SHOW_LOG, doShowLogs);
}
